/**
 * List and download POS diagnostic bundles uploaded from terminals.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabase.js';
import { Callout, PageHeader } from '../ui/index.js';

const BUCKET = 'pos-diagnostics';

interface DiagFile {
  name: string;
  path: string;
  size: number | null;
  updatedAt: string | null;
}

interface DiagBundle {
  folder: string;
  label: string;
  updatedAt: string | null;
  files: DiagFile[];
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function folderLabel(name: string): string {
  // 2026-07-08T16-32-15-123Z_username → readable label
  const [ts, user] = name.split('_');
  if (!user) return name;
  const when = ts?.replace(/T/, ' ').replace(/-/g, (m, i) => (i > 9 ? ':' : m)) ?? ts;
  return `${user} · ${when}`;
}

export function PosDiagnosticsView({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [bundles, setBundles] = useState<DiagBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError('');
    try {
      const { data: folders, error: listErr } = await supabase.storage
        .from(BUCKET)
        .list(clientId, { limit: 50, sortBy: { column: 'created_at', order: 'desc' } });
      if (listErr) throw listErr;

      const results: DiagBundle[] = [];
      for (const folder of folders ?? []) {
        if (!folder.name) continue;
        // Storage folders have id === null; files at this level are skipped.
        if (folder.id !== null) continue;
        const prefix = `${clientId}/${folder.name}`;
        const { data: files, error: filesErr } = await supabase.storage
          .from(BUCKET)
          .list(prefix, { limit: 20, sortBy: { column: 'name', order: 'asc' } });
        if (filesErr) throw filesErr;
        const mapped: DiagFile[] = (files ?? [])
          .filter((f) => f.name && f.name !== '.emptyFolderPlaceholder')
          .map((f) => ({
            name: f.name!,
            path: `${prefix}/${f.name}`,
            size: f.metadata?.size ?? null,
            updatedAt: f.updated_at ?? f.created_at ?? null,
          }));
        const latest = mapped.reduce<string | null>(
          (max, f) => (!max || (f.updatedAt && f.updatedAt > max) ? f.updatedAt : max),
          null,
        );
        results.push({
          folder: prefix,
          label: folderLabel(folder.name),
          updatedAt: latest ?? folder.updated_at ?? folder.created_at ?? null,
          files: mapped,
        });
      }
      setBundles(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download(path: string) {
    if (!supabase) return;
    setDownloading(path);
    try {
      const { data, error: urlErr } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, 3600);
      if (urlErr || !data?.signedUrl) throw urlErr ?? new Error('No signed URL');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="page ui-stack">
      <PageHeader
        title={t('posDiagnostics.title')}
        subtitle={t('posDiagnostics.hint')}
        actions={
          <button className="btn-secondary" onClick={() => void load()} disabled={loading}>
            {loading ? t('common.loading') : t('common.retry')}
          </button>
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}

      {loading && bundles.length === 0 ? (
        <p>{t('common.loading')}</p>
      ) : bundles.length === 0 ? (
        <p className="muted">{t('posDiagnostics.empty')}</p>
      ) : (
        <div className="diag-bundles">
          {bundles.map((b) => (
            <section key={b.folder} className="diag-bundle-card">
              <div className="diag-bundle-head">
                <strong>{b.label}</strong>
                <span className="muted" style={{ fontSize: 12 }}>
                  {fmtTime(b.updatedAt)}
                </span>
              </div>
              <ul className="diag-file-list">
                {b.files.map((f) => (
                  <li key={f.path}>
                    <span>{f.name}</span>
                    {f.size != null && (
                      <span className="muted" style={{ fontSize: 12 }}>
                        {Math.round(f.size / 1024)} KB
                      </span>
                    )}
                    <button
                      className="link"
                      disabled={downloading === f.path}
                      onClick={() => void download(f.path)}
                    >
                      {downloading === f.path ? '…' : t('posDiagnostics.download')}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
